// Service 8 — Marriage & civil registration for foreigners in Thailand.
// Category definitions only (client-bundle safe, no corpus import).

export type MarriageFaqCategory =
  | "basics"
  | "affirmation-embassy"
  | "translation-legalization"
  | "prenup-property"
  | "divorce-remarriage"
  | "name-title-records"
  | "use-abroad"
  | "visa-after-marriage"
  | "child-birth-records"
  | "phuket-practical"
  | "by-country";

export interface MarriageCategoryDef {
  slug: MarriageFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const MARRIAGE_FAQ_CATEGORIES: MarriageCategoryDef[] = [
  {
    slug: "basics",
    titleTh: "พื้นฐานการจดทะเบียนสมรสไทย–ต่างชาติ ที่สำนักทะเบียนอำเภอ/เขต",
    titleEn: "Thai–Foreign Marriage Registration Basics at the District Office",
    descTh:
      "เงื่อนไขตามประมวลกฎหมายแพ่งและพาณิชย์ อายุขั้นต่ำ 18 ปี การสมรสของบุคคลเพศเดียวกันตามกฎหมายสมรสเท่าเทียม เอกสารที่ต้องนำไปยื่น พยาน ล่าม และขั้นตอนที่สำนักทะเบียน",
    descEn:
      "Requirements under the Civil and Commercial Code, the minimum age of 18, same-sex marriage under the marriage-equality amendment, documents to bring, witnesses, interpreters and the district-office procedure.",
    matchTh: /(สำนักทะเบียน|อำเภอ|เขต|อายุ 18|สมรสเท่าเทียม|เพศเดียวกัน|พยาน|ล่าม|คร\.2|คร\.3|เงื่อนไขการสมรส|ขั้นตอนจดทะเบียนสมรส)/i,
    matchEn: /(district office|amphur|amphoe|khet|age of 18|marriage equality|same-sex|witness|interpreter|kor ror 2|kor ror 3|registration procedure)/i,
  },
  {
    slug: "affirmation-embassy",
    titleTh: "หนังสือรับรองสถานภาพการสมรสจากสถานทูต (Affirmation of Freedom to Marry)",
    titleEn: "Embassy Affirmation of Freedom to Marry",
    descTh:
      "การขอหนังสือรับรองว่ามีอิสระที่จะสมรสจากสถานเอกอัครราชทูตหรือสถานกงสุลของคุณในไทย เอกสารประกอบ การนัดหมาย อายุการใช้งาน และกรณีที่สถานทูตไม่ออกให้",
    descEn:
      "Obtaining an affirmation or statutory declaration of freedom to marry from your embassy or consulate in Thailand, supporting documents, appointments, validity and what to do when an embassy does not issue one.",
    matchTh: /(สถานทูต|สถานเอกอัครราชทูต|สถานกงสุล|หนังสือรับรองสถานภาพ|อิสระที่จะสมรส|affirmation|statutory declaration|โสด|ใบรับรองความเป็นโสด)/i,
    matchEn: /(embassy|consulate|affirmation|statutory declaration|freedom to marry|single status|certificate of no impediment|CNI)/i,
  },
  {
    slug: "translation-legalization",
    titleTh: "แปลเอกสารและรับรองนิติกรณ์ที่กรมการกงสุลก่อนยื่นสำนักทะเบียน",
    titleEn: "Translation and MFA Legalisation Before Filing",
    descTh:
      "ลำดับ แปลเป็นไทย → รับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล → ยื่นสำนักทะเบียน ข้อผิดพลาดเรื่องการสะกดชื่อ วันที่ และตราประทับที่ทำให้เอกสารถูกตีกลับ",
    descEn:
      "The order of operations — Thai translation, certification by the Legalisation Division of the Department of Consular Affairs, then filing at the district office — plus the spelling, date and seal errors that get files rejected.",
    matchTh: /(แปลเป็นไทย|รับรองคำแปล|กรมการกงสุล|นิติกรณ์|สะกดชื่อ|ตราประทับ|ถนนแจ้งวัฒนะ|ทับซ้อน|MFA)/i,
    matchEn: /(translation into thai|certified translation|department of consular affairs|legalisation|legalization|spelling|seal|MFA)/i,
  },
  {
    slug: "prenup-property",
    titleTh: "สัญญาก่อนสมรส สินสมรส และทรัพย์สินของคู่สมรสต่างชาติ",
    titleEn: "Prenuptial Agreements, Marital Property and Foreign Spouses",
    descTh:
      "สัญญาก่อนสมรสต้องจดแจ้งพร้อมการจดทะเบียนสมรสจึงจะสมบูรณ์ ความต่างระหว่างสินส่วนตัวกับสินสมรส และหนังสือยืนยันของคู่สมรสต่างชาติเมื่อคู่สมรสไทยซื้อที่ดิน",
    descEn:
      "A prenuptial agreement must be registered together with the marriage to be effective, the difference between personal and marital property, and the foreign-spouse declaration used when a Thai spouse buys land.",
    matchTh: /(สัญญาก่อนสมรส|สินสมรส|สินส่วนตัว|ทรัพย์สิน|ซื้อที่ดิน|กรมที่ดิน|หนังสือยืนยัน|prenup)/i,
    matchEn: /(prenuptial|marital property|personal property|buy land|land department|declaration|matrimonial)/i,
  },
  {
    slug: "divorce-remarriage",
    titleTh: "หย่า การสมรสใหม่ และการรับรองสถานภาพหลังหย่า",
    titleEn: "Divorce, Remarriage and Proving Your Status",
    descTh:
      "หย่าโดยความยินยอมที่สำนักทะเบียนกับหย่าโดยคำพิพากษา เอกสารที่ต้องใช้เมื่อเคยสมรสหรือหย่าในต่างประเทศ และการรับรองคำพิพากษาต่างประเทศเพื่อใช้ในไทย",
    descEn:
      "Divorce by mutual consent at the district office versus divorce by court judgment, documents required when you married or divorced abroad, and recognition of foreign judgments for use in Thailand.",
    matchTh: /(หย่า|ทะเบียนการหย่า|คำพิพากษา|สมรสใหม่|คู่สมรสเสียชีวิต|มรณบัตร|หย่าในต่างประเทศ)/i,
    matchEn: /(divorce|decree|court judgment|remarry|remarriage|widow|death certificate|divorced abroad)/i,
  },
  {
    slug: "name-title-records",
    titleTh: "เปลี่ยนนามสกุล คำนำหน้านาม และการแก้ทะเบียนราษฎรหลังสมรส",
    titleEn: "Surname, Title and Civil-Registration Updates After Marriage",
    descTh:
      "สิทธิเลือกใช้นามสกุลของคู่สมรสตาม พ.ร.บ.ชื่อบุคคล สิทธิเลือกคำนำหน้า นาง/นางสาว ตาม พ.ร.บ.คำนำหน้านามหญิง และการแก้บัตรประชาชน ทะเบียนบ้าน หนังสือเดินทาง",
    descEn:
      "The right to take a spouse's surname under the Person Name Act, the choice between Mrs and Miss under the Women's Titles Act, and updating the Thai ID card, house registration and passport.",
    matchTh: /(นามสกุล|คำนำหน้า|นาง|นางสาว|บัตรประชาชน|ทะเบียนบ้าน|หนังสือเดินทาง|เปลี่ยนชื่อ)/i,
    matchEn: /(surname|last name|title|mrs|miss|thai id card|house registration|passport|change of name)/i,
  },
  {
    slug: "use-abroad",
    titleTh: "นำทะเบียนสมรสไทยไปใช้ต่างประเทศ — แปล นิติกรณ์ และ Apostille",
    titleEn: "Using a Thai Marriage Certificate Abroad — Translation, Legalisation, Apostille",
    descTh:
      "ขอคัดสำเนาทะเบียนสมรส (คร.2/คร.3) แปลเป็นอังกฤษ รับรองที่กรมการกงสุล และรับรองต่อที่สถานทูตปลายทาง พร้อมกรอบเวลาที่อนุสัญญา Apostille จะมีผลกับไทย",
    descEn:
      "Certified copies of the marriage registration, English translation, MFA legalisation and destination-embassy legalisation, plus the timeline for the Apostille Convention taking effect for Thailand.",
    matchTh: /(ใช้ต่างประเทศ|คัดสำเนา|คร\.2|คร\.3|Apostille|อนุสัญญา|สถานทูตปลายทาง|รับรองต่อ)/i,
    matchEn: /(use abroad|certified copy|apostille|convention|destination embassy|counter-legalis|counter-legaliz)/i,
  },
  {
    slug: "visa-after-marriage",
    titleTh: "วีซ่าและการอยู่ต่อหลังสมรส — Non-Immigrant O และการต่ออายุ 1 ปี",
    titleEn: "Visa and Stay After Marriage — Non-Immigrant O and Annual Extension",
    descTh:
      "การเปลี่ยนประเภทการตรวจลงตราเป็น Non-Immigrant O ฐานสมรส หลักเกณฑ์การเงินตามคำสั่งสำนักงานตำรวจแห่งชาติ การรายงานตัว 90 วัน TM.30 และผลของการหย่าต่อสิทธิอยู่ต่อ",
    descEn:
      "Changing to a Non-Immigrant O visa on the marriage ground, the financial criteria in the Royal Thai Police order, 90-day reporting, TM.30 and how divorce affects the right to stay.",
    matchTh: /(วีซ่า|Non-Immigrant O|อยู่ต่อ|ต่ออายุ|400,000|40,000|90 วัน|TM\.?30|ตรวจคนเข้าเมือง|ตม\.)/i,
    matchEn: /(visa|non-immigrant o|extension of stay|400,000|40,000|90-day|TM\.?30|immigration)/i,
  },
  {
    slug: "child-birth-records",
    titleTh: "สูติบัตร การรับรองบุตร และสัญชาติของบุตรที่เกิดในไทย",
    titleEn: "Birth Certificates, Legitimation and Nationality of Children Born in Thailand",
    descTh:
      "แจ้งเกิดภายใน 15 วัน สูติบัตรไทยและการแจ้งเกิดต่อสถานทูตของบิดา/มารดาต่างชาติ การรับรองบุตรที่สำนักทะเบียนหรือโดยคำพิพากษา และอำนาจปกครองบุตร",
    descEn:
      "Reporting a birth within 15 days, the Thai birth certificate and consular report of birth abroad, legitimation at the district office or by court order, and parental power.",
    matchTh: /(สูติบัตร|แจ้งเกิด|รับรองบุตร|อำนาจปกครอง|สัญชาติของบุตร|บุตร)/i,
    matchEn: /(birth certificate|report a birth|legitimation|parental power|child(ren)? nationality|custody)/i,
  },
  {
    slug: "phuket-practical",
    titleTh: "ปฏิบัติจริงในภูเก็ต — สำนักทะเบียนที่รับเรื่อง ล่าม และการนัดหมาย",
    titleEn: "Practical Notes for Phuket — District Offices, Interpreters and Appointments",
    descTh:
      "อำเภอเมืองภูเก็ต ถลาง และกะทู้ ระยะเวลาโดยประมาณเมื่อเอกสารครบ การเดินเรื่องแปลและนิติกรณ์จากภูเก็ต และการนัดหมายสถานกงสุลกิตติมศักดิ์ในพื้นที่",
    descEn:
      "Mueang Phuket, Thalang and Kathu district offices, realistic timelines once documents are complete, running translation and legalisation from Phuket, and honorary-consulate appointments on the island.",
    matchTh: /(ภูเก็ต|ถลาง|กะทู้|ป่าตอง|กงสุลกิตติมศักดิ์|นัดหมาย)/i,
    matchEn: /(phuket|thalang|kathu|patong|honorary consul|appointment)/i,
  },
  {
    slug: "by-country",
    titleTh: "แยกตามสัญชาติคู่สมรส — เอกสารสถานทูตแต่ละประเทศและการใช้ทะเบียนสมรสในต่างประเทศ",
    titleEn: "By Nationality — Embassy Requirements and Using the Thai Certificate Abroad",
    descTh:
      "เอกสารรับรองสถานภาพของสหรัฐฯ อังกฤษ ออสเตรเลีย เยอรมนี ฝรั่งเศส ญี่ปุ่น จีน รัสเซีย อินเดีย เกาหลี รวมถึงการบันทึกฐานะแห่งครอบครัว (คร.22) เมื่อจดทะเบียนสมรสในต่างประเทศ",
    descEn:
      "Status documents for US, UK, Australian, German, French, Japanese, Chinese, Russian, Indian and Korean nationals, plus recording a foreign marriage in Thailand (Kor Ror 22).",
    matchTh:
      /(อเมริกัน|สหรัฐ|อังกฤษ|ออสเตรเลีย|เยอรมัน|เยอรมนี|ฝรั่งเศส|ญี่ปุ่น|จีน|รัสเซีย|อินเดีย|เกาหลี|คร\.22|บันทึกฐานะแห่งครอบครัว|Apostille|สัญชาติ)/i,
    matchEn:
      /(us citizen|british|australian|german|french|japanese|chinese|russian|indian|korean|kor ror 22|family status record|apostille|nationality)/i,
  },
];

export function getMarriageCategoryDef(slug: string): MarriageCategoryDef | undefined {
  return MARRIAGE_FAQ_CATEGORIES.find((c) => c.slug === slug);
}
